"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Capability } from "./prompt-types";
import { Card, CardContent } from "@/components/ui/card";
import CapabilityPill from "./capability-pill";
import CodeExample from "./code-example";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Maximize2, X } from "lucide-react";

type BoundingBox = {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
};

type BoundingBoxData = BoundingBox[];

const buildNeonPalette = (hue: number) => ({
  glow: `hsla(${hue}, 100%, 60%, 0.7)`,
  core: `hsla(${hue}, 100%, 75%, 0.9)`,
  coreHighlight: `hsla(${hue}, 100%, 90%, 1)`,
  fill: `hsl(${hue}, 100%, 65%)`,
});

export default function InferenceDetect({
  image,
  result,
  query,
  duration,
  imageFile,
}: {
  image: string;
  result: BoundingBoxData;
  query: string;
  duration?: number;
  imageFile?: File;
}) {
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const detections = useMemo(() => (Array.isArray(result) ? result : []), [result]);
  const [isExpanded, setIsExpanded] = useState(false);
  const defsIdBaseRef = useRef(`detect-${Math.random().toString(36).slice(2)}`);
  const fallbackHueRef = useRef<number>(Math.floor(Math.random() * 360));
  const hueMapRef = useRef<Map<number, number>>(new Map());
  const [pulseKey, setPulseKey] = useState(0);

  const isDone = detections.length > 0;

  // Load image once to determine scaled dimensions
  useEffect(() => {
    let cancelled = false;
    const img = new window.Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      if (cancelled) return;
      const viewportLimit =
        typeof window !== "undefined" ? Math.min(window.innerWidth - 64, 1800) : 1200;
      const MAX_DIMENSION = isExpanded ? Math.max(512, viewportLimit) : 512;
      let scaledWidth = img.width;
      let scaledHeight = img.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      setDimensions({ width: scaledWidth, height: scaledHeight });
    };

    img.src = image;

    return () => {
      cancelled = true;
    };
  }, [image, isExpanded]);

  // Trigger pulse animation the first time detections appear
  useEffect(() => {
    if (isDone) {
      setPulseKey((k) => k + 1);
    }
  }, [isDone]);

  const overlayList = useMemo(() => {
    const { width, height } = dimensions;
    if (width === 0 || height === 0) return [];
    return detections
      .map((box, idx) => {
        const bboxWidthPx = (box.x_max - box.x_min) * width;
        const bboxHeightPx = (box.y_max - box.y_min) * height;
        if (bboxWidthPx <= 0 || bboxHeightPx <= 0) return null;
        return {
          idx,
          x: box.x_min * width,
          y: box.y_min * height,
          width: bboxWidthPx,
          height: bboxHeightPx,
        };
      })
      .filter(Boolean) as Array<{
      idx: number;
      x: number;
      y: number;
      width: number;
      height: number;
    }>;
  }, [detections, dimensions]);

  const labelText = `${overlayList.length} found`;

  const renderOverlays = (modal?: boolean) =>
    overlayList.map((overlay) => {
      const idx = overlay.idx;
      if (!hueMapRef.current.has(idx)) {
        const assignedHue = (fallbackHueRef.current + idx * 137) % 360;
        hueMapRef.current.set(idx, assignedHue);
      }
      const hue = hueMapRef.current.get(idx)!;
      const palette = buildNeonPalette(hue);
      const suffix = modal ? "modal" : "inline";
      const gradientId = `${defsIdBaseRef.current}-${suffix}-grad-${idx}`;
      const clipPathId = `${defsIdBaseRef.current}-${suffix}-clip-${idx}`;
      const glowId = `${defsIdBaseRef.current}-${suffix}-glow-${idx}`;
      const hoverScale = isDone ? 1.25 : 1;
      const transform = `translate(${overlay.x} ${overlay.y}) scale(${overlay.width} ${overlay.height})`;

      // Rect path in unit space; we use transform to scale/translate into place
      const unitRectPath = "M0 0 H1 V1 H0 Z";

      return (
        <svg
          key={`${suffix}-${idx}`}
          className="absolute inset-0 w-full h-full"
          width="100%"
          height="100%"
          viewBox={`0 0 ${dimensions.width || 1} ${dimensions.height || 1}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ pointerEvents: isDone ? "auto" : "none" }}
        >
          <defs>
            <clipPath id={clipPathId} clipPathUnits="userSpaceOnUse">
              <path d={unitRectPath} transform={transform} />
            </clipPath>

            <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="0.02" result="blur" />
              <feColorMatrix
                in="blur"
                type="matrix"
                values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 0.8 0"
              />
            </filter>

            <linearGradient
              id={gradientId}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%">
                <animate
                  attributeName="stop-color"
                  values={`${palette.core};${palette.coreHighlight};${palette.core}`}
                  dur="2s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="100%">
                <animate
                  attributeName="stop-color"
                  values={`${palette.coreHighlight};${palette.core};${palette.coreHighlight}`}
                  dur="2s"
                  repeatCount="indefinite"
                />
              </stop>
              <animateTransform
                attributeName="gradientTransform"
                type="translate"
                values="0 0; 1 0; 0 0"
                dur="2s"
                repeatCount="indefinite"
              />
            </linearGradient>
          </defs>

          {/* Reveal full-brightness image inside the rectangle */}
          <image
            href={image}
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid meet"
            clipPath={`url(#${clipPathId})`}
            style={{ pointerEvents: "none" }}
          />

          <g transform={transform}>
            <motion.g
              style={{ transformOrigin: "50% 50%" }}
              whileHover={isDone ? { scale: hoverScale } : undefined}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              {isDone && (
                <motion.path
                  key={`fill-${pulseKey}-${suffix}-${idx}`}
                  d={unitRectPath}
                  fill={palette.fill}
                  stroke="none"
                  vectorEffect="non-scaling-stroke"
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: [0, 0.2, 0],
                    transition: {
                      duration: 0.7,
                      times: [0, 0.35, 1],
                      ease: "easeOut",
                    },
                  }}
                />
              )}

              <motion.path
                key={`glow-${pulseKey}-${suffix}-${idx}`}
                d={unitRectPath}
                vectorEffect="non-scaling-stroke"
                stroke={palette.glow}
                strokeWidth={20}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                filter={`url(#${glowId})`}
                initial={{ opacity: 0.7, strokeWidth: 20 }}
                animate={
                  isDone
                    ? {
                        opacity: [0.7, 1, 0],
                        strokeWidth: [20, 26, 0],
                        transition: {
                          duration: 0.5,
                          times: [0, 0.2, 1],
                          ease: "easeOut",
                        },
                      }
                    : { opacity: 0.7, strokeWidth: 20 }
                }
              />
              <path
                d={unitRectPath}
                vectorEffect="non-scaling-stroke"
                stroke={`url(#${gradientId})`}
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </motion.g>
          </g>
        </svg>
      );
    });

  const renderFrame = (modal?: boolean) => (
    <div
      className="relative overflow-hidden rounded-lg bg-black/5"
      style={{
        width: "100%",
        aspectRatio:
          dimensions.width && dimensions.height
            ? `${dimensions.width} / ${dimensions.height}`
            : "1 / 1",
      }}
    >
      <svg
        className="absolute inset-0 w-full h-full"
        width="100%"
        height="100%"
        viewBox={`0 0 ${dimensions.width || 1} ${dimensions.height || 1}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <image
          href={image}
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          style={{
            filter: isDone ? "brightness(0.35)" : "none",
            transition: "filter 150ms ease",
          }}
        />
      </svg>

      {renderOverlays(modal)}

      <div className="pointer-events-none absolute left-2 top-2 z-10 rounded-md bg-black/60 px-2 py-1 text-[10px] font-medium text-white shadow-sm backdrop-blur-sm">
        {labelText}
      </div>

      {modal ? (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-3 top-3 z-10 h-8 w-8 rounded-md bg-black/60 text-white hover:bg-black/70"
          onClick={() => setIsExpanded(false)}
        >
          <X className="h-4 w-4" aria-hidden />
          <span className="sr-only">Close expanded image</span>
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2 z-10 h-7 w-7 rounded-md bg-black/60 text-white hover:bg-black/70"
          onClick={() => setIsExpanded(true)}
        >
          <Maximize2 className="h-4 w-4" aria-hidden />
          <span className="sr-only">Expand image</span>
        </Button>
      )}
    </div>
  );

  return (
    <>
      <Card>
        <CardContent className="mt-6">
          <div className="flex flex-col gap-4 sm:gap-6">
            <div className="flex flex-row items-center gap-2">
              <CapabilityPill capability={Capability.ObjectDetection} />
              <div className="text-sm break-words">{query}</div>
            </div>

            <div className="w-full max-w-[600px]">{renderFrame(false)}</div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              {imageFile && (
                <CodeExample
                  capability={Capability.ObjectDetection}
                  prompt={query}
                  imageFile={imageFile}
                />
              )}
              {duration !== undefined && (
                <div className="text-xs text-muted-foreground">
                  Response time: {duration.toFixed(0)}ms
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent
          className="p-0 border-none bg-transparent shadow-none w-[90vw] max-w-[90vw] sm:max-w-[90vw] md:max-w-[90vw] lg:max-w-[90vw] xl:max-w-[90vw] max-h-[90vh] overflow-hidden"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Expanded detection result</DialogTitle>
          </DialogHeader>
          <div className="relative w-full">
            <div
              className="relative overflow-hidden rounded-lg bg-black/5 w-full"
              style={{
                width: "100%",
                maxWidth: "100%",
                aspectRatio:
                  dimensions.width && dimensions.height
                    ? `${dimensions.width} / ${dimensions.height}`
                    : "1 / 1",
              }}
            >
              {renderFrame(true)}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
